import React, {useRef, useState} from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import {
    withStyles,
    Tooltip,
    IconButton,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
} from '@material-ui/core';
import { FindInPage, Settings, CloudDownload, FileCopy } from '@material-ui/icons';
import DeviceDependentSettingList from './DeviceDependentSettingList';
import { snackActions } from '../../sc-utilities/SnackBar';
import { hasPermission, createHistory } from '../../sc-utilities';
import config from "../../../config/config";
import {download} from "../../sc-utilities/download.utils";

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    progressBarr: {
        width: '100%',
        height: 5
    }
});

const DeviceDependentSetting = props => {
    const tableRef = useRef();
    const { classes, columns, translations, permissions, activeCustomerID } = props;
    let visible = columns && columns['DeviceDependentSetting'];
    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, id: null, redirectPath: "" });
    const [settingsModal, setSettingsModal] = useState({ open: false, settings: null });

    const tableActionsRow = [
        {
            icon: 'FindInPage',
            tooltip: translations['device_dependent_setting_details'] || 'Device dependent setting details',
            onClick: (event, rowData) => goToDeviceDependentSettingDetails(event, rowData),
            hidden: !hasPermission(permissions, 'device_dependent_setting_load')
        },
        {
            icon: 'Settings',
            tooltip: translations['view_device_dependent'] || 'View device dependent setting',
            onClick: (event, rowData) => goToSettings(event, rowData)
        },
        {
            icon: 'CloudDownload',
            tooltip: translations['export_json'] || 'Export setting (JSON)',
            onClick: (event, rowData) => handleDownload(event, rowData, translations)
        },
        {
            icon: 'FileCopy',
            tooltip: translations['create_from_this'] || 'Create from this',
            onClick: (event, rowData) => goToCreateFromThis(event, rowData),
            hidden: !hasPermission(permissions, 'device_dependent_setting_load')
        }
    ];

    let id = null

    const getIcon = icon => {
        switch (icon) {
            case 'FindInPage':
                return <FindInPage id={`device_dependent_setting_details_${id}`} />
            case 'Settings':
                return <Settings id={`settings_${id}`} />
            case 'CloudDownload':
                return <CloudDownload id={`download_${id}`} />
            case 'FileCopy':
                return <FileCopy id={`create_from_this_${id}`} />
            default:
                return <> </>
        }
    };

    const tableColumns = [
        {
            title: translations["action"] || 'Action', field: 'Action',
            hidden: visible ? visible['Action'] : false,
            cellStyle: { minWidth: 220, maxWidth: 220 },
            filtering: false,
            render: rowData => {
                return tableActionsRow.map(action => {
                    id = rowData.id;
                    if (action.hidden) {
                        return null;
                    }
                    return (
                        <Tooltip key={`${action.icon}-${rowData.id}`} title={action.tooltip}>
                            <IconButton
                                component='div'
                                onClick={(event) => action.onClick(event, rowData)}
                            >
                                {
                                    getIcon(action.icon)
                                }
                            </IconButton>
                        </Tooltip>
                    )
                })
            }
        },
        {
            title: translations["dependent_type"] || "Dependent type", field: 'DependentType',
            hidden: visible ? visible['DependentType'] : false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.DependentType ? rowData.DependentType : translations['short_not_assigned'] || 'n/a'}</Typography>;
            },
        }
    ];

    const goToDeviceDependentSettingDetails = (_event, rowData) => {
        createHistory();
        setRedirectOptions({ redirect: true, ID: rowData.id, redirectPath: `/admin/deviceDependentSettingsEdit?id=${rowData.id}` });
    };

    const goToSettings = (_event, rowData) => {
        let dependentSetting = JSON.parse(rowData.DependentSetting);
        let text = [];
        for (var key in dependentSetting) {
            text.push(`${key} - ${dependentSetting[key]}`);
        }
        setSettingsModal({ open: true, settings: text });
    }

    const goToCreateFromThis = (_event, rowData) => {
        createHistory();
        setRedirectOptions({ redirect: true, ID: rowData.id, redirectPath: `/admin/deviceDependentSettingsCreateFromThis?id=${rowData.id}` });
    };

    const goToUploadDeviceDependentSetting = () => {
        createHistory();
        setRedirectOptions({ redirect: true, ID: null, redirectPath: `/admin/deviceDependentSettingUpload` });
    };

    const handleDownload = async (_event, rowData) => {
        const objectURL = `${config.backend_domain}:${config.backend_port}/dependent_settings/export?ID=${rowData.id}&ActiveCustomerID=${activeCustomerID}`;
        let exportDataSuccessful = await download(objectURL);
        tableRef.current && tableRef.current.onQueryChange();
        if (!exportDataSuccessful) {
            return snackActions.snackBarComponent(translations['download_error_msg'] || `Could not download file `, 'error');
        }
    };

    const redirectHandler = (path, id = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, id: id, redirectPath: path });
    };

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    return (
        <div className={classes.root}>
            {
                hasPermission(permissions, 'device_dependent_setting_list') ?
                    <DeviceDependentSettingList
                        goToUploadDeviceDependentSetting={goToUploadDeviceDependentSetting}
                        tableColumns={tableColumns}
                        redirectHandler={redirectHandler}
                        tableRef={tableRef}
                    /> : null
            }
            <Dialog
                open={settingsModal.open}
                onClose={() => {
                    return setSettingsModal({ open: false, settings: null })
                }}
                aria-labelledby="form-dialog-title"
                disableBackdropClick={false}
                disableEscapeKeyDown={false}
                disableEnforceFocus={true}
                fullWidth={true}
            >
                <DialogTitle id="form-dialog-title">{translations['device_dependent_settings'] || 'Device dependent settings'}</DialogTitle>
                <DialogContent>
                    {
                        typeof settingsModal === 'object' && settingsModal.settings ? settingsModal.settings.map(setting => (
                            <Typography key={setting}>{setting}</Typography>)) : ''
                    }
                </DialogContent>
            </Dialog>
        </div>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || []
    };
};

export default connect(
    mapStateToProps,
    null
)(withStyles(styles)(DeviceDependentSetting));
